import { store } from '@/pinia/store'
import { req } from '@/api'
import { tblsMapKey } from '@/config'
import { appidCodeInit, getUrlData, isWeiXin, loadWxJsSDK } from '@/utils'
import { jsonp } from 'vue-jsonp'

// ==================== 默认兜底地址 ====================

const localAddress = {
  adcode: '320200',
  cityCode: '320200',
  cityName: '无锡市',
  longitude: '120.301663',
  latitude: '31.574729',
  locationAddress: '秦邦宪故居',
  position: { lng: '120.301663', lat: '31.574729' },
  pois: [],
  districtCityCode: '320213',
  districtCityName: '梁溪区',
  districtCityLevel: 2
}

// ==================== 请求封装 ====================

const TMapWS = 'https://apis.map.qq.com'

const callTMap = async (path: string, params: Record<string, string | number>): Promise<any> => {
  const url = new URL(path, TMapWS)
  url.searchParams.set('key', tblsMapKey)
  url.searchParams.set('output', 'jsonp')
  Object.entries(params).forEach(([k, v]) => {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v))
  })
  // vue-jsonp 自动生成 callback 函数名并以默认参数名 "callback" 拼接到 URL
  const data = await jsonp<any>(url.toString(), {}, 10000)
  if (data.status !== 0) throw new Error(`[TlbsMap] API错误: ${data.message || data.status}`)
  return data
}

// ==================== 逆地理编码（经纬度→地址） ====================

const getGeocoder = async (points: { lng: string | number; lat: string | number; adcode?: string }) => {
  try {
    console.log('[TlbsMap] getGeocoder:逆地理编码', points)
    const data = await callTMap('/ws/geocoder/v1', {
      location: `${points.lat},${points.lng}`,
      get_poi: 1,
    })
    console.log('[TlbsMap] getGeocoder:返回', data)
    const r = data.result
    const thirdCode = points.adcode || r.ad_info?.adcode || ''

    const res = await translateThirdCode(String(thirdCode))
    const { cityInfo, districtInfo } = res
    const ac = r.address_component || {}

    const resolvedAddress = {
      position: { lng: String(points.lng), lat: String(points.lat) },
      adcode: thirdCode,
      longitude: String(points.lng),
      latitude: String(points.lat),
      cityName: cityInfo.name || ac.city || ac.province || '',
      cityCode: cityInfo.code,
      locationAddress: r.pois?.[0]?.title || r.formatted_addresses?.recommend || r.address || '',
      pois: mapPois(r.pois || []),
      districtCityCode: districtInfo.code || thirdCode,
      districtCityName: districtInfo.name || ac.district || '',
      districtCityLevel: districtInfo.cityLevel,
    }
    console.log('[TlbsMap] getGeocoder:成功', resolvedAddress)
    return resolvedAddress
  } catch (e) {
    console.error('[TlbsMap] getGeocoder:失败', e)
    return localAddress
  }
}

// ==================== 定位 ====================

// 微信 JSSDK 初始化结果缓存（避免重复 config 覆盖其他模块的权限声明）
let wxJssdkPromise: Promise<void> | null = null

// 初始化微信 JSSDK：加载脚本 + wx.config（含 getLocation 及分享权限，避免覆盖 useShare 的 config）
const ensureWxJssdk = (): Promise<void> => {
  if (wxJssdkPromise) return wxJssdkPromise
  wxJssdkPromise = (async () => {
    await loadWxJsSDK()
    const wx = (window as any).wx
    if (!wx) throw new Error('window.wx 不存在')
    const res = await req.getWxConfig({
      url: location.href.split('#')[0],
      appidCode: appidCodeInit(getUrlData('appCode')),
    })
    const { appId, timestamp, nonceStr, signature } = res.data
    const storeData = store()
    storeData.isShowLoading = false
    wx.config({
      debug: false,
      appId,
      timestamp: timestamp - 0,
      nonceStr,
      signature,
      jsApiList: [
        'getLocation',
        'onMenuShareTimeline',
        'onMenuShareAppMessage',
        'onMenuShareQQ',
        'updateAppMessageShareData',
        'updateTimelineShareData',
        'wx-open-launch-app',
        'wx-open-launch-weapp',
      ],
      openTagList: ['wx-open-launch-app', 'wx-open-launch-weapp'],
    })
  })()
  // 失败时清空缓存以便下次重试
  wxJssdkPromise.catch(() => { wxJssdkPromise = null })
  return wxJssdkPromise
}

// 微信定位（含超时与 error 兜底，绝不挂起）
const getWxLocation = (): Promise<{ latitude: number; longitude: number } | null> =>
  new Promise((resolve) => {
    const storeData = store()
    const timer = setTimeout(() => {
      console.error('[TlbsMap] getWxLocation:定位超时')
      resolve(null)
    }, 10000)
    const settle = (fn: () => void) => {
      clearTimeout(timer)
      fn()
    }
    const wx = (window as any).wx
    if (!wx) {
      storeData.isShowLoading = false
      console.error('[TlbsMap] getWxLocation:window.wx 不存在')
      settle(() => resolve(null))
      return
    }
    // config 失败兜底
    wx.error((res: any) => {
      storeData.isShowLoading = false
      console.error('[TlbsMap] getWxLocation:JSSDK config 失败', res)
      settle(() => {
        resolve(null)
      })
    })
    wx.ready(() => {
      storeData.isShowLoading = false
      wx.getLocation({
        type: 'gcj02',
        success: (res: any) => {
          console.log('[TlbsMap] getWxLocation:微信定位成功', res)
          settle(() => resolve({ latitude: res.latitude, longitude: res.longitude }))
        },
        fail: () => {
          console.error('[TlbsMap] getWxLocation:微信定位失败')
          settle(() => {
            resolve(null)
          })
        },
        cancel: () => {
          console.log('[TlbsMap] getWxLocation:微信定位取消')
          settle(() => {
            resolve(null)
          })
        },
      })
    })
  })

// WGS84 转 GCJ02（火星坐标）：腾讯地图使用 GCJ02 体系，浏览器 GPS 原始坐标为 WGS84，直接使用会有 100-500m 偏移
const wgs84ToGcj02 = (lng: number, lat: number) => {
  const PI = Math.PI
  const a = 6378245.0 // 长半轴
  const ee = 0.00669342162296594323 // 偏心率平方

  const transformLat = (x: number, y: number): number => {
    let ret = -100.0 + 2.0 * x + 3.0 * y + 0.2 * y * y + 0.1 * x * y + 0.2 * Math.sqrt(Math.abs(x))
    ret += ((20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0) / 3.0
    ret += ((20.0 * Math.sin(y * PI) + 40.0 * Math.sin((y / 3.0) * PI)) * 2.0) / 3.0
    ret += ((160.0 * Math.sin((y / 12.0) * PI) + 320 * Math.sin((y * PI) / 30.0)) * 2.0) / 3.0
    return ret
  }

  const transformLng = (x: number, y: number): number => {
    let ret = 300.0 + x + 2.0 * y + 0.1 * x * x + 0.1 * x * y + 0.1 * Math.sqrt(Math.abs(x))
    ret += ((20.0 * Math.sin(6.0 * x * PI) + 20.0 * Math.sin(2.0 * x * PI)) * 2.0) / 3.0
    ret += ((20.0 * Math.sin(x * PI) + 40.0 * Math.sin((x / 3.0) * PI)) * 2.0) / 3.0
    ret += ((150.0 * Math.sin((x / 12.0) * PI) + 300.0 * Math.sin((x / 30.0) * PI)) * 2.0) / 3.0
    return ret
  }

  // 不在中国境内的坐标不转换
  if (lng < 72.004 || lng > 137.8347 || lat < 0.8293 || lat > 55.8271) {
    return { latitude: lat, longitude: lng }
  }
  let dLat = transformLat(lng - 105.0, lat - 35.0)
  let dLng = transformLng(lng - 105.0, lat - 35.0)
  const radLat = (lat / 180.0) * PI
  let magic = Math.sin(radLat)
  magic = 1 - ee * magic * magic
  const sqrtMagic = Math.sqrt(magic)
  dLat = (dLat * 180.0) / (((a * (1 - ee)) / (magic * sqrtMagic)) * PI)
  dLng = (dLng * 180.0) / ((a / sqrtMagic) * Math.cos(radLat) * PI)
  return {
    latitude: Number((lat + dLat).toFixed(6)),
    longitude: Number((lng + dLng).toFixed(6)),
  }
}

// WGS84 转 GCJ02：主路径调用腾讯坐标转换 API，失败时本地算法 wgs84ToGcj02 兜底
const translateWgs84ToGcj02 = async (lng: number, lat: number) => {
  try {
    const data = await callTMap('/ws/coord/v1/translate', {
      locations: `${lat},${lng}`,
      type: 1, // 1 = GPS 原始坐标（WGS84）
    })
    console.log('[TlbsMap] translateWgs84ToGcj02:腾讯转换返回', data)
    const r = data.locations?.[0]
    if (r?.lat && r?.lng) {
      const loc = { latitude: Number(r.lat), longitude: Number(r.lng) }
      console.log('[TlbsMap] translateWgs84ToGcj02:腾讯转换成功', loc)
      return loc
    }
    console.error('[TlbsMap] translateWgs84ToGcj02:返回无结果')
  } catch (e) {
    console.error('[TlbsMap] translateWgs84ToGcj02:腾讯转换失败,本地算法兜底', e)
  }
  const loc = wgs84ToGcj02(lng, lat)
  console.log('[TlbsMap] translateWgs84ToGcj02:本地算法转换', loc)
  return loc
}

const getLocation = async () => {
  const storeData = store()
  storeData.isShowLoading = true

  let loc: { latitude: number; longitude: number } | null = null

  // 1. 微信环境优先微信定位
  if (isWeiXin()) {
    console.log('[TlbsMap] getLocation:微信定位')
    try {
      await ensureWxJssdk()
      loc = await getWxLocation()
    } catch (e) {
      console.error('[TlbsMap] getLocation:微信定位异常', e)
    }
  }

  // 2. 微信定位失败/非微信 → 浏览器GPS定位
  if (!loc?.latitude) {
    try {
      // 浏览器 GPS 坐标（WGS84）
      const pos = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 30000, maximumAge: 60000, enableHighAccuracy: true })
      })
      console.log('[TlbsMap] getLocation:浏览器 GPS 坐标（WGS84）', pos)
      const gpsinfo = {
        latitude: Number(pos.coords.latitude.toFixed(6)),
        longitude: Number(pos.coords.longitude.toFixed(6)),
      }
      // WGS84 转 GCJ02，与逆地理编码坐标系一致（腾讯API优先，本地算法兜底）
      loc = await translateWgs84ToGcj02(gpsinfo.longitude, gpsinfo.latitude)
      console.log('[TlbsMap] getLocation:浏览器GPS定位成功(GCJ02)', loc)
    } catch (e) {
      console.log('[TlbsMap] getLocation:浏览器GPS定位失败', e)
    }
  }

  // 3. 最后 IP 兜底
  if (!loc?.latitude) {
    console.log('[TlbsMap] getLocation:降级IP定位')
    try {
      const data = await callTMap('/ws/location/v1/ip', {})
      console.log('[TlbsMap] getLocation:ip返回', data)
      const r = data.result?.location
      if (r?.lat && r?.lng) {
        loc = { latitude: r.lat, longitude: r.lng }
      }
    } catch (e) {
      console.error('[TlbsMap] getLocation:IP定位失败', e)
    }
  }

  storeData.isShowLoading = false
  if (loc?.latitude && loc?.longitude) {
    console.log('[TlbsMap] getLocation:定位成功,逆地理编码')
    return await getGeocoder({ lng: loc.longitude, lat: loc.latitude })
  }
  return localAddress
}

// ==================== POI 结果转换 ====================

const mapPois = (list: any[]) =>
  ((list || []).filter((p: any) => p?.address) || []).map((p: any) => ({
    ...p,
    adcode: p?.ad_info?.adcode || '',
    name: p.title,
    location: p.location ? { lng: p.location.lng, lat: p.location.lat } : undefined,
    distance: p._distance ? Math.round(p._distance) : 0,
  }))

// ==================== 地点搜索 ====================

const searchPlace = async (keyword: string, boundary: string) => {
  try {
    console.log('[TlbsMap] searchPlace:搜索', keyword, boundary)
    const data = await callTMap('/ws/place/v1/search', {
      keyword,
      boundary,
      filter: 'category=210000,280000',
      page_size: 20,
    })
    console.log('[TlbsMap] searchPlace:返回', data)
    const result = mapPois(data.data || [])
    console.log('[TlbsMap] searchPlace:成功', result.length, '条')
    return result
  } catch (e) {
    console.error('[TlbsMap] searchPlace:失败', e)
    return []
  }
}

// ==================== 驾车路线 ====================

const drivingMap = async (params: any) => {
  return new Promise(async (resolve) => {
    try {
      const from = `${params.start}`
      const to = `${params.end}`
      console.log('[TlbsMap] drivingMap:驾车路线', from, '→', to)
      const data = await callTMap('/ws/direction/v1/driving', { from, to })
      console.log('[TlbsMap] drivingMap:返回', data)
      const route = data.result?.routes?.[0] || {}
      console.log('[TlbsMap] drivingMap:成功', route ? '有路线' : '无路线')
      // polyline 坐标解压
      const coords = route?.polyline || []
      const paths = []
      const kr = 1000000
      for (let i = 2; i < coords.length; i++) {
        coords[i] = Number(coords[i - 2]) + Number(coords[i]) / kr
      }
      //将解压后的坐标放入点串数组paths中
      for (let i = 0; i < coords.length; i += 2) {
        paths.push({
          lat: coords[i],
          lng: coords[i + 1],
        })
      }
      resolve({
        distance: route?.distance || 0,
        duration: route?.duration || 0,
        paths: paths || [],
      })
    } catch (e) {
      console.error('[TlbsMap] drivingMap:失败', e)
      resolve({})
    }
  })
}

// ==================== 周边推荐（explore） ====================

// 按经纬度获取周边高热度地点（无须关键词，用于发送位置/地点签到场景）
const getExplorePois = async (params: { lng: string | number; lat: string | number; radius?: number }) => {
  try {
    console.log('[TlbsMap] getExplorePois:周边推荐', params)
    const data = await callTMap('/ws/place/v1/explore', {
      boundary: `nearby(${params.lat},${params.lng},1000,1)`,
      page_size: 20,
      filter: 'category=210000,280000',
    })
    console.log('[TlbsMap] getExplorePois:返回', data)
    const result = mapPois(data.data || [])
    console.log('[TlbsMap] getExplorePois:成功', result.length, '条')
    return result
  } catch (e) {
    console.error('[TlbsMap] getExplorePois:失败', e)
    return []
  }
}

// ==================== 编码转换 ====================

const translateThirdCode = async (thirdCode: string) => {
  const storeData = store()
  const fallback = {
    cityInfo: { code: storeData.locationData.cityCode || '320200', name: storeData.locationData.cityName || '无锡市' },
    districtInfo: { code: storeData.locationData.districtCityCode || '320200', name: storeData.locationData.districtCityName || '无锡市', cityLevel: 2 },
  }
  if (!thirdCode) return fallback
  try {
    const res = await req.getCityDistrictByMapCode({ thirdType: '2', thirdCityCode: thirdCode || storeData.locationData.districtCityCode || 320213 })
    if (res?.code === 0) {
      console.log('[TlbsMap] translateThirdCode:成功', res.data.districtInfo?.code, res.data.districtInfo?.name)
      return res.data
    }
    console.error('[TlbsMap] translateThirdCode:接口失败', res?.msg)
  } catch (e) {
    console.error('[TlbsMap] translateThirdCode:异常', e)
  }
  return fallback
}

// ==================== 主入口 ====================

const initTlbsMap = async (type: number = 0, params?: any) => {
  try {
    console.log('[TlbsMap] initTlbsMap:调用', type, params)
    switch (type) {
      case 1:
        return await getLocation()
      case 2:
        return await searchPlace(params?.keyWord || '', params?.boundary || '')
      case 3:
        return await drivingMap(params)
      case 4:
        return await getExplorePois(params)
      case 5:
        return await getGeocoder(params)
      default:
        return localAddress
    }
  } catch (e) {
    console.error('[TlbsMap] initTlbsMap:执行失败', e)
    return localAddress
  }
}

export { localAddress, initTlbsMap, translateThirdCode }
